import { useAppSelector } from '@/store/hooks';
import {
  selectIsAuthenticated,
  selectIsAuthLoading,
  selectPermissions,
  type NVXPermission,
} from '@/store/slices/authSlice';
import { hasAnyPermission } from '@/lib/auth/utils';
import { AuthLoading } from './AuthLoading';

interface AuthGuardProps {
  children: React.ReactNode;
  requiredPermissions?: NVXPermission[];
  loadingComponent?: React.ReactNode;
  unauthorizedPath?: string;
}

/**
 * A render-only authorization boundary.
 *
 * AuthenticationController owns every login and redirect decision. Keeping
 * this component free of side effects prevents competing login redirects.
 */
export function AuthGuard({
  children,
  requiredPermissions,
  loadingComponent,
}: AuthGuardProps) {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isLoading = useAppSelector(selectIsAuthLoading);
  const permissions = useAppSelector(selectPermissions);

  if (isLoading || !isAuthenticated) {
    return <>{loadingComponent ?? <AuthLoading />}</>;
  }

  const hasRequiredPermissions =
    !requiredPermissions ||
    requiredPermissions.length === 0 ||
    hasAnyPermission(permissions, requiredPermissions);

  if (!hasRequiredPermissions) return null;

  return <>{children}</>;
}
